import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { providerAccessAPI } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  FileText, 
  Loader2, 
  Calendar,
  Clock,
  Eye,
  Activity,
  Download,
  Filter
} from 'lucide-react'

interface ProviderStats {
  totalSessions: number
  activeSessions: number
  uniquePatients: number
  recordsViewed: number
  period: {
    startDate: string
    endDate: string
  }
}

interface StaffStats {
  id: string
  staff_code: string
  first_name: string
  last_name: string
  role: string
  department?: string
  provider_access_sessions: { count: number }[]
  provider_access_audit: { count: number }[]
}

interface AuditEntry {
  id: string
  action: string
  provider: string
  staff: string
  staff_role: string
  record_type?: string
  timestamp: string
  metadata?: Record<string, unknown>
}

export default function ProviderAnalyticsPage() {
  const { toast } = useToast()
  const [stats, setStats] = useState<ProviderStats | null>(null)
  const [staffStats, setStaffStats] = useState<StaffStats[]>([])
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  })

  useEffect(() => {
    fetchAnalytics()
  }, [dateRange])

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true)
      
      // Fetch provider stats
      const statsResponse = await providerAccessAPI.getProviderStats({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      })
      
      if (statsResponse.success) {
        setStats(statsResponse.data.stats)
      }

      // Fetch staff stats
      const staffResponse = await providerAccessAPI.getStaffStats()
      if (staffResponse.success) {
        setStaffStats(staffResponse.data.staff_stats || [])
      }

      // Fetch recent audit log
      const auditResponse = await providerAccessAPI.getAuditLog({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        limit: 50
      })
      if (auditResponse.success) {
        setAuditLog(auditResponse.data.audit_log || [])
      }

    } catch (error) {
      console.error('Failed to fetch analytics:', error)
      // Set default empty values
      setStats({
        totalSessions: 0,
        activeSessions: 0,
        uniquePatients: 0,
        recordsViewed: 0,
        period: { startDate: dateRange.startDate, endDate: dateRange.endDate }
      })
      setStaffStats([])
      setAuditLog([])
      toast({
        title: "Could not load analytics",
        description: "Please try again later.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    setDateRange(prev => ({ ...prev, [field]: value }))
  }

  const exportAuditLog = async (format: 'csv' | 'json') => {
    setIsExporting(true)
    try {
      const data = auditLog.map(entry => ({
        id: entry.id,
        action: entry.action,
        staff: entry.staff,
        staff_role: entry.staff_role,
        record_type: entry.record_type || 'N/A',
        timestamp: entry.timestamp,
        metadata: JSON.stringify(entry.metadata || {})
      }))

      let content: string
      let filename: string
      let mimeType: string

      if (format === 'csv') {
        const headers = ['ID', 'Action', 'Staff', 'Role', 'Record Type', 'Timestamp', 'Metadata']
        const rows = data.map(d => [
          d.id,
          d.action,
          d.staff,
          d.staff_role,
          d.record_type,
          d.timestamp,
          d.metadata
        ])
        content = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n')
        filename = `audit-log-${dateRange.startDate}-to-${dateRange.endDate}.csv`
        mimeType = 'text/csv'
      } else {
        content = JSON.stringify(data, null, 2)
        filename = `audit-log-${dateRange.startDate}-to-${dateRange.endDate}.json`
        mimeType = 'application/json'
      }

      const blob = new Blob([content], { type: mimeType })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      toast({
        title: "Export Complete",
        description: `Audit log exported as ${format.toUpperCase()}`,
      })
    } catch (error) {
      console.error('Export failed:', error)
      toast({
        title: "Export Failed",
        description: "Could not export audit log. Please try again.",
        variant: "destructive"
      })
    } finally {
      setIsExporting(false)
    }
  }

  const getActionBadgeColor = (action: string) => {
    switch (action) {
      case 'session_started':
        return 'bg-emerald-100 text-emerald-700'
      case 'records_viewed':
      case 'record_viewed':
        return 'bg-blue-100 text-blue-700'
      case 'session_ended':
        return 'bg-gray-100 text-gray-700'
      case 'access_revoked':
        return 'bg-red-100 text-red-700'
      case 'staff_added':
        return 'bg-purple-100 text-purple-700'
      case 'staff_deactivated':
        return 'bg-amber-100 text-amber-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const formatAction = (action: string) => {
    return action.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#D64463]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">Access statistics and audit trail for your organization</p>
        </div>

        {/* Date Range Filter */}
        <div className="flex items-center gap-3 bg-white p-3 rounded-xl border shadow-sm">
          <Filter className="h-4 w-4 text-gray-400" />
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
              className="h-9 w-36"
            />
            <span className="text-gray-400">to</span>
            <Input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
              className="h-9 w-36"
            />
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#D64463]/10 rounded-xl">
                <Activity className="h-6 w-6 text-[#D64463]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalSessions || 0}</p>
                <p className="text-sm text-gray-500">Total Sessions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-xl">
                <Clock className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.activeSessions || 0}</p>
                <p className="text-sm text-gray-500">Active Sessions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.uniquePatients || 0}</p>
                <p className="text-sm text-gray-500">Patients Accessed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Eye className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.recordsViewed || 0}</p>
                <p className="text-sm text-gray-500">Records Viewed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staff Statistics */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[#D64463]" />
            Staff Access Statistics
          </CardTitle>
          <CardDescription>
            Per-staff breakdown of patient access and record views
          </CardDescription>
        </CardHeader>
        <CardContent>
          {staffStats.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Staff Member</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Role</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Department</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Sessions</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {staffStats.map((staff) => {
                    const sessionCount = staff.provider_access_sessions?.[0]?.count || 0
                    const actionCount = staff.provider_access_audit?.[0]?.count || 0
                    return (
                      <tr key={staff.id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#D64463] to-[#9C27B0] flex items-center justify-center text-white text-xs font-semibold">
                              {staff.first_name.charAt(0)}{staff.last_name.charAt(0)}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{staff.first_name} {staff.last_name}</p>
                              <p className="text-xs text-gray-500 font-mono">{staff.staff_code}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{staff.role}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{staff.department || '-'}</td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant="secondary">{sessionCount}</Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant="secondary">{actionCount}</Badge>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No staff statistics available</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Access History / Audit Log */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#D64463]" />
              Access History
            </CardTitle>
            <CardDescription>
              Recent access events and audit trail
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportAuditLog('csv')}
              disabled={isExporting || auditLog.length === 0}
            >
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportAuditLog('json')}
              disabled={isExporting || auditLog.length === 0}
            >
              <Download className="h-4 w-4 mr-1" />
              JSON
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {auditLog.length > 0 ? (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {auditLog.map((entry) => (
                <div key={entry.id} className="flex items-start gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="shrink-0">
                    <Badge className={getActionBadgeColor(entry.action)}>
                      {formatAction(entry.action)}
                    </Badge>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">{entry.staff || 'Unknown'}</span>
                      {entry.staff_role && (
                        <span className="text-gray-500"> ({entry.staff_role})</span>
                      )}
                    </p>
                    {entry.record_type && (
                      <p className="text-xs text-gray-500 mt-1">
                        Record type: {entry.record_type}
                      </p>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 shrink-0">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(entry.timestamp).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3" />
                      {new Date(entry.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No access history available for this period</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-slate-50 to-gray-50">
        <CardContent className="py-6 text-center">
          <p className="text-gray-600">
            Analytics data is updated in real-time as staff members access patient records.
            All access events are logged for HIPAA compliance.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
